import React from 'react';
import { type IntlShape } from 'react-intl-next';
import BulletListIcon from '@atlaskit/icon/core/migration/bulleted-list--bullet-list';
import DecisionIcon from '@atlaskit/icon/core/migration/decision';
import DiscoverFilledIcon from '@atlaskit/icon/core/migration/compass--discover-filled';
import LightbulbIcon from '@atlaskit/icon/core/migration/lightbulb';
import PageIcon from '@atlaskit/icon/core/migration/page';
import PeopleGroupIcon from '@atlaskit/icon/core/migration/people-group';
import { B300, G300, N20, P300, T300, Y300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import SprintIcon from '@atlaskit/icon/core/migration/sprint';
import { type Size } from '@atlaskit/icon';

import messages from './messages';
import { TemplateIcon } from './styled';
import {
	type Template,
	TemplateType,
	type TemplateBase,
	type ConfluenceTemplate,
} from '../../containers/template/types';
import { BlueprintModuleCompleteKeys, ItemModuleCompleteKeys } from '../../common/types';
import { getDateString } from './utils';
import { getTutorialContent } from './tutorial-content/index';
import { getExperimentValue } from '../../common/feature-gates';

// Default Theme Preview Images
import DefaultBlankPreExpandPreview from '../../assets/pre-expand/default/blank.svg';
import DefaultBlankPostExpandPreview from '../../assets/post-expand/default/blank.png';
import DefaultDecisionPostExpandPreview from '../../assets/post-expand/default/decision.png';
import DefaultProductRequirementsPostExpandPreview from '../../assets/post-expand/default/product-requirements.png';
import DefaultDecisionPreExpandPreview from '../../assets/pre-expand/default/decision.svg';
import DefaultMeetingNotesPreExpandPreview from '../../assets/pre-expand/default/meeting-notes.svg';
import DefaultMeetingNotesPostExpandPreview from '../../assets/post-expand/default/meeting-notes.png';
import DefaultProductRequirementsPreExpandPreview from '../../assets/pre-expand/default/product-requirements.svg';
import DefaultRetrospectivePreExpandPreview from '../../assets/pre-expand/default/retrospective.svg';
import DefaultRetrospectivePostExpandPreview from '../../assets/post-expand/default/retrospective.png';
import DefaultWelcomeToProjectPagesPreExpandPreview from '../../assets/pre-expand/default/welcome-to-project-pages.svg';
import DefaultWelcomeToProjectPagesPostExpandPreview from '../../assets/post-expand/default/welcome-to-project-pages.png';
import DefaultWelcomeToProjectPagesPreExpandSporkRebrandingPreview from '../../assets/pre-expand/default/welcome-to-project-pages-spork-rebranding.svg';
import DefaultWelcomeToProjectPagesPostExpandSporkRebrandingPreview from '../../assets/post-expand/default/welcome-to-project-pages-spork-rebranding.png';

// Dark Theme Preview Images
import DarkBlankPostExpandPreview from '../../assets/post-expand/dark/blank-dark.png';
import DarkBlankPreExpandPreview from '../../assets/pre-expand/dark/blank-dark.svg';
import DarkDecisionPostExpandPreview from '../../assets/post-expand/dark/decision-dark.png';
import DarkDecisionPreExpandPreview from '../../assets/pre-expand/dark/decision-dark.svg';
import DarkMeetingNotesPostExpandPreview from '../../assets/post-expand/dark/meeting-notes-dark.png';
import DarkMeetingNotesPreExpandPreview from '../../assets/pre-expand/dark/meeting-notes-dark.svg';
import DarkProductRequirementsPostExpandPreview from '../../assets/post-expand/dark/product-requirements-dark.png';
import DarkProductRequirementsPreExpandPreview from '../../assets/pre-expand/dark/product-requirements-dark.svg';
import DarkRetrospectivePostExpandPreview from '../../assets/post-expand/dark/retrospective-dark.png';
import DarkRetrospectivePreExpandPreview from '../../assets/pre-expand/dark/retrospective-dark.svg';
import DarkWelcomeToProjectPagesPostExpandPreview from '../../assets/post-expand/dark/welcome-to-project-pages-dark.png';
import DarkWelcomeToProjectPagesPreExpandPreview from '../../assets/pre-expand/dark/welcome-to-project-pages-dark.svg';
import DarkWelcomeToProjectPagesPreExpandSporkRebrandingPreview from '../../assets/pre-expand/dark/welcome-to-project-pages-spork-rebranding-dark.svg';
import DarkWelcomeToProjectPagesPostExpandSporkRebrandingPreview from '../../assets/post-expand/dark/welcome-to-project-pages-spork-rebranding-dark.png';

interface IconRender {
	size: Size;
	color?: string;
}

const defaultTemplates: ConfluenceTemplate[] = [
	{
		type: TemplateType.Confluence,
		key: 'blankPage',
		name: messages.blankPageTemplateName,
		description: messages.blankPageTemplateDescription,
		blueprintModuleCompleteKey: null,
		itemModuleCompleteKey: ItemModuleCompleteKeys.BLANK_PAGE,
		iconRender: ({ size, color }: IconRender) => (
			<TemplateIcon size={size} backgroundColor={color}>
				<PageIcon label="blank" color={token('color.icon.inverse', '#fff')} LEGACY_size={size} />
			</TemplateIcon>
		),
		defaultIconColor: token('color.background.accent.blue.bolder', B300),
		preview: {
			preExpand: {
				default: DefaultBlankPreExpandPreview,
				dark: DarkBlankPreExpandPreview,
			},
			postExpand: {
				default: DefaultBlankPostExpandPreview,
				dark: DarkBlankPostExpandPreview,
			},
		},
	},
	{
		type: TemplateType.Confluence,
		key: 'productRequirements',
		name: messages.productRequirementsTemplateName,
		description: messages.productRequirementsTemplateDescription,
		blueprintModuleCompleteKey: BlueprintModuleCompleteKeys.PRODUCT_REQUIREMENTS,
		itemModuleCompleteKey: ItemModuleCompleteKeys.PRODUCT_REQUIREMENTS,
		iconRender: ({ size, color }: IconRender) => (
			<TemplateIcon size={size} backgroundColor={color}>
				<BulletListIcon
					label="product-requirements"
					color={token('color.icon.inverse', '#fff')}
					LEGACY_size={size}
				/>
			</TemplateIcon>
		),
		defaultIconColor: token('color.background.accent.purple.bolder', P300),
		preview: {
			preExpand: {
				default: DefaultProductRequirementsPreExpandPreview,
				dark: DarkProductRequirementsPreExpandPreview,
			},
			postExpand: {
				dark: DarkProductRequirementsPostExpandPreview,
				default: DefaultProductRequirementsPostExpandPreview,
			},
		},
		getTitle: () => `${getDateString()} Product requirements`,
	},
	{
		type: TemplateType.Confluence,
		key: 'decision',
		name: messages.decisionTemplateName,
		description: messages.decisionTemplateDescription,
		blueprintModuleCompleteKey: BlueprintModuleCompleteKeys.DECISION,
		itemModuleCompleteKey: ItemModuleCompleteKeys.DECISION,
		iconRender: ({ size, color }: IconRender) => (
			<TemplateIcon size={size} backgroundColor={color}>
				<DecisionIcon
					label="decision"
					color={token('color.icon.inverse', '#fff')}
					LEGACY_size={size}
				/>
			</TemplateIcon>
		),
		defaultIconColor: token('color.background.accent.green.bolder', G300),
		preview: {
			preExpand: {
				default: DefaultDecisionPreExpandPreview,
				dark: DarkDecisionPreExpandPreview,
			},
			postExpand: {
				dark: DarkDecisionPostExpandPreview,
				default: DefaultDecisionPostExpandPreview,
			},
		},
		getTitle: () => `${getDateString()} Decision`,
	},
	{
		type: TemplateType.Confluence,
		key: 'meetingNotes',
		name: messages.meetingNotesTemplateName,
		description: messages.meetingNotesTemplateDescription,
		blueprintModuleCompleteKey: BlueprintModuleCompleteKeys.MEETING_NOTES,
		itemModuleCompleteKey: ItemModuleCompleteKeys.MEETING_NOTES,
		iconRender: ({ size, color }: IconRender) => (
			<TemplateIcon size={size} backgroundColor={color}>
				<PeopleGroupIcon
					label="meeting-notes"
					color={token('color.icon.inverse', '#fff')}
					LEGACY_size={size}
				/>
			</TemplateIcon>
		),
		defaultIconColor: token('color.background.accent.teal.bolder', T300),
		preview: {
			preExpand: {
				default: DefaultMeetingNotesPreExpandPreview,
				dark: DarkMeetingNotesPreExpandPreview,
			},
			postExpand: {
				dark: DarkMeetingNotesPostExpandPreview,
				default: DefaultMeetingNotesPostExpandPreview,
			},
		},
	},
	{
		type: TemplateType.Confluence,
		key: 'retrospective',
		name: messages.retrospectiveTemplateName,
		description: messages.retrospectiveTemplateDescription,
		blueprintModuleCompleteKey: BlueprintModuleCompleteKeys.RESTROSPECTIVE,
		itemModuleCompleteKey: ItemModuleCompleteKeys.RESTROSPECTIVE,
		iconRender: ({ size, color }: IconRender) => (
			<TemplateIcon size={size} backgroundColor={color}>
				<SprintIcon
					label="retrospective"
					color={token('color.icon.inverse', '#fff')}
					LEGACY_size={size}
				/>
			</TemplateIcon>
		),
		defaultIconColor: token('color.background.accent.yellow.bolder', Y300),
		preview: {
			preExpand: {
				default: DefaultRetrospectivePreExpandPreview,
				dark: DarkRetrospectivePreExpandPreview,
			},
			postExpand: {
				dark: DarkRetrospectivePostExpandPreview,
				default: DefaultRetrospectivePostExpandPreview,
			},
		},
		getTitle: () => `${getDateString()} Retrospective`,
	},
];

export const getDefaultTemplates = (): ConfluenceTemplate[] => defaultTemplates;

export const moreTemplatesBase: TemplateBase = {
	key: 'more',
	iconRender: ({ size, color }: IconRender) => (
		<TemplateIcon size={size} backgroundColor={color}>
			<DiscoverFilledIcon label="more" color={token('color.icon', '#000')} LEGACY_size={size} />
		</TemplateIcon>
	),
	defaultIconColor: token('color.background.neutral', N20),
};

const reorderedWeight: {
	[K in ItemModuleCompleteKeys]: number;
} = {
	[ItemModuleCompleteKeys.MEETING_NOTES]: 1,
	[ItemModuleCompleteKeys.PRODUCT_REQUIREMENTS]: 2,
	[ItemModuleCompleteKeys.RESTROSPECTIVE]: 3,
	[ItemModuleCompleteKeys.DECISION]: 4,
	[ItemModuleCompleteKeys.BLANK_PAGE]: 5,
};

export const getBasePreExpandTemplates = (): Template[] => {
	const templates = [...getDefaultTemplates()];
	if (
		getExperimentValue('jsw_project_pages_reorder_templates_v2', 'not-enrolled') === 'variation'
	) {
		templates.sort(
			(a, b) =>
				reorderedWeight[a.itemModuleCompleteKey || ItemModuleCompleteKeys.BLANK_PAGE] -
				reorderedWeight[b.itemModuleCompleteKey || ItemModuleCompleteKeys.BLANK_PAGE],
		);
	}

	return [
		...templates,
		{
			...moreTemplatesBase,
			type: TemplateType.Link,
			name: messages.templatesPreExpandMore,
			url: 'https://www.atlassian.com/software/confluence/templates',
			renderShortcutIcon: false,
		},
	];
};

export const getTutorialTemplate = (
	intl: IntlShape,
	isProjectPagesSporkRebrandingEnabled: boolean = false,
): Template => {
	const tutorialContent = getTutorialContent(intl, isProjectPagesSporkRebrandingEnabled);
	return {
		type: TemplateType.Confluence,
		key: 'tutorial',
		name: messages.tutorialPageName,
		title: messages.tutorialPageName,
		description: messages.tutorialPageDescription,
		blueprintModuleCompleteKey: null,
		itemModuleCompleteKey: null,
		iconRender: ({ size, color }: IconRender) => (
			<TemplateIcon size={size} backgroundColor={color}>
				<LightbulbIcon
					label="blank"
					color={token('color.icon.inverse', '#fff')}
					LEGACY_size={size}
				/>
			</TemplateIcon>
		),
		defaultIconColor: token('color.background.accent.purple.bolder', P300),
		preview: {
			preExpand: {
				default: isProjectPagesSporkRebrandingEnabled
					? DefaultWelcomeToProjectPagesPreExpandSporkRebrandingPreview
					: DefaultWelcomeToProjectPagesPreExpandPreview,
				dark: isProjectPagesSporkRebrandingEnabled
					? DarkWelcomeToProjectPagesPreExpandSporkRebrandingPreview
					: DarkWelcomeToProjectPagesPreExpandPreview,
			},
			postExpand: {
				default: isProjectPagesSporkRebrandingEnabled
					? DefaultWelcomeToProjectPagesPostExpandSporkRebrandingPreview
					: DefaultWelcomeToProjectPagesPostExpandPreview,
				dark: isProjectPagesSporkRebrandingEnabled
					? DarkWelcomeToProjectPagesPostExpandSporkRebrandingPreview
					: DarkWelcomeToProjectPagesPostExpandPreview,
			},
		},
		content: tutorialContent,
	};
};
